import {Button, Chip, Grid, useTheme} from "@mui/material";
import React, {Dispatch, SetStateAction, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {
    CellClassParams,
    ColDef,
    ICellRendererParams,
    IHeaderGroupParams,
    ITooltipParams,
    ProcessCellForExportParams
} from "ag-grid-community";
import {heightDynamicMatrix, i18nConvertNumberWithDecimals} from "../../../utils";
import {DEFAULT_MATRIX_COL_DEF, maxNumberYupLimit, SET_SNACKBAR} from "../../../constants";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useTranslation} from "react-i18next";
import {GridApi} from "ag-grid-enterprise";
import DialogComponent from "../../../common/components/DialogComponent";
import {
    AncillaryExceptionDetail,
    MatrixDictionary,
    MatrixElementInterface
} from "../../../api/types/ancillaryExceptionDetail";
import {useRentalPeriodRepository} from "../../../api/repositories/RentalPeriodRepository";
import {useNavigate} from "react-router-dom";
import {DialogInterface, SelectElementModelFE} from "../../../api/types/generic";
import {RentalPeriodEntityResponse} from "../../../api/types/rentalPeriods";
import {useConstantsRepository} from "../../../api/repositories/ConstantsRepository";
import {Sell} from "../../../api/types/constants";
import CustomBanner from "../../../common/components/CustomBanner";
import {AncillaryExceptionEntityResponse} from "../../../api/types/ancillaryException";
import {useAncillaryExceptionDetailRepository} from "../../../api/repositories/AncillaryExceptionDetailRepository";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {useAncillaryDetailRepository} from "../../../api/repositories/AncillaryDetailRepository";
import {AncillaryDetail} from "../../../api/types/ancillaryDetail";
import {TaxCodeObject} from "../../../api/types/ancillary";
import {useRecoilState} from "recoil";
import {ancillaryExceptionAtom} from "../../../state/ancillaryExceptionRecoil";
import NumberCustomEditorComponent from "../../../common/components/NumberCustomEditorComponent";
import PercentageCellEditor from "../../../common/components/PercentageCellEditor";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import dayjs from "dayjs";
import {useAcrissRepository} from "../../../api/repositories/AcrissRepository";
import CheckboxRenderer from "../../Rate/Matrix/CheckboxRenderer";
import { convertToCents } from "../../../../../util/state/commonFunctions";
import {ancillaryServiceItem} from "../../../state/ancillariesRecoil";

const AncillaryExceptionMatrix = ({
                                      ancillaryExceptionId,
                                      ancillaryExceptionInUse,
                                      readOnly,
                                      setValue
                                  }: {
    ancillaryExceptionId: number | null
    ancillaryExceptionInUse: boolean
    readOnly: boolean
    setValue: Function
}): JSX.Element => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Theme
    const theme = useTheme();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [ancillaryException] = useRecoilState<AncillaryExceptionEntityResponse | null>(ancillaryExceptionAtom);

    // Use State
    const [matrix, setMatrix] = useState<MatrixDictionary>({});
    const [initialMatrix, setInitialMatrix] = useState<MatrixDictionary>({});
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    // Use Navigate
    const navigate = useNavigate();

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    // Use State
    const [ancillaryExceptionDetails, setAncillaryExceptionDetails] = useState<AncillaryExceptionDetail[]>([]);
    const [carGroupRows, setCarGroupRows] = useState<string[]>([]);
    const [, setAllCarGroupRows] = useState<string[]>([]);
    const [initialCarGroupRows, setInitialCarGroupRows] = useState<string[]>([]);
    const [rentalPeriodColumns, setRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [, setAllRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [initialRentalPeriodColumns, setInitialRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [sellList, setSellList] = useState<SelectElementModelFE[]>([]);
    const [serviceItem] = useRecoilState<any>(ancillaryServiceItem);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Repository Hooks
    const {
        getAncillaryExceptionDetails,
        persistAncillaryExceptionAndDetails
    } = useAncillaryExceptionDetailRepository();
    const {getAncillaryDetailsFromCopyAncillary} = useAncillaryDetailRepository();
    const {getRentalPeriods, getRentalPeriodsSorted} = useRentalPeriodRepository();
    const {getSell} = useConstantsRepository();
    const {getOrderedAcriss, orderCarGroupsByAcrissOrder} = useAcrissRepository();

    const daysOfTheWeekUppercase = [
        {code: 1, label: dayjs().day(1).format("dddd").toUpperCase()},
        {code: 2, label: dayjs().day(2).format("dddd").toUpperCase()},
        {code: 3, label: dayjs().day(3).format("dddd").toUpperCase()},
        {code: 4, label: dayjs().day(4).format("dddd").toUpperCase()},
        {code: 5, label: dayjs().day(5).format("dddd").toUpperCase()},
        {code: 6, label: dayjs().day(6).format("dddd").toUpperCase()},
        {code: 7, label: dayjs().day(0).format("dddd").toUpperCase()}
    ];

    const getMatrixInfo = async () => {
        // UPDATE ECCEZIONE
        if (ancillaryExceptionId && ancillaryException) {
            try {
                // const ancillaryException: AncillaryExceptionEntityResponse = await getAncillaryException(ancillaryExceptionId);
                // setAncillaryException(ancillaryException);

                let ancillaryExceptionDetails: AncillaryExceptionDetail[] = (await getAncillaryExceptionDetails(ancillaryExceptionId))
                    .map((ancillaryExceptionDetail: AncillaryExceptionDetail) => {
                        return {
                            ...ancillaryExceptionDetail,
                            price: i18nConvertNumberWithDecimals(Number(ancillaryExceptionDetail.price) / 100)
                        };
                    });

                //se ancillary exception details è vuoto, reinizializzare la matrice da accessorio
                if (ancillaryExceptionDetails?.length === 0) {
                    const ancillaryDetails: AncillaryDetail[] = await getAncillaryDetailsFromCopyAncillary({
                        originalAncillaryId: ancillaryException?.prcAncillary?.id as number,
                        destinationAncillaryId: ancillaryException?.prcAncillary?.id as number,
                        fixedDiscount: ancillaryException?.relatedDiscountIncreaseFixed ? Number(ancillaryException.relatedDiscountIncreaseFixed.toString().replace(",", ".")) : null,
                        percentDiscount: ancillaryException?.relatedDiscountIncreasePercentage ? Number(ancillaryException.relatedDiscountIncreasePercentage) : null
                    });
                    const ancillaryExceptionDetailsResponse: AncillaryExceptionDetail[] = ancillaryDetails.map((ancillaryDetail: AncillaryDetail) => {
                        const ancillaryExceptionDetail: AncillaryExceptionDetail = {
                            id: ancillaryDetail.id,
                            prcAncillaryException: ancillaryException,
                            carGroup: ancillaryDetail.carGroup,
                            prcRentalPeriod: ancillaryDetail.prcRentalPeriod,
                            price: ancillaryDetail.price,
                            sell: ancillaryDetail.sell,
                            forfait: ancillaryDetail?.prcAncillary?.tpUnit === "RENTAL" || ancillaryDetail?.prcAncillary?.subtype === "DELIVERY" || ancillaryDetail?.prcAncillary?.subtype === "COLLECTION" ? false : ancillaryDetail.forfait
                        };
                        return ancillaryExceptionDetail;
                    });
                    ancillaryExceptionDetails = mapAncillaryExceptionDetails({ancillaryExceptionDetails: ancillaryExceptionDetailsResponse});
                }

                setAncillaryExceptionDetails(ancillaryExceptionDetails);

                const sellList = await getSell();
                setSellList(sellList.map((sell: Sell) => {
                    return {
                        code: sell.sellType,
                        label: sell.description
                    };
                }));

                let rentalPeriods: RentalPeriodEntityResponse[];
                let carGroups: string[];
                let allRentalPeriods: RentalPeriodEntityResponse[];
                let allCarGroups: string[];

                // ancillary con matrice già esistente
                if (ancillaryExceptionDetails.length > 0) {
                    const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({ancillaryExceptionDetails});
                    rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                    carGroups = carGroupsAndRentalPeriods.carGroups;
                    allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                        return rp.tpUnit === ancillaryException.tpUnit;
                    });
                    allRentalPeriods = await getRentalPeriodsSorted(allRentalPeriods)

                    // togliere CAR_GROUPS || quando si pusha
                    // allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    allCarGroups = (await getOrderedAcriss(ancillaryException.legalEntity)).map(el => el.acriss);
                    allCarGroups = await orderCarGroupsByAcrissOrder(allCarGroups, ancillaryException.legalEntity)
                } else {
                    rentalPeriods = allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                        return rp.tpUnit === ancillaryException.tpUnit;
                    });
                    rentalPeriods = await getRentalPeriodsSorted(rentalPeriods)

                    // togliere CAR_GROUPS || quando si pusha
                    // carGroups = allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    carGroups = allCarGroups = (await getOrderedAcriss(ancillaryException.legalEntity)).map(el => el.acriss);
                    carGroups = await orderCarGroupsByAcrissOrder(carGroups, ancillaryException.legalEntity)
                }

                setCarGroupsAndRentalPeriodsAndMatrix({
                    rentalPeriods,
                    carGroups,
                    allRentalPeriods,
                    allCarGroups,
                    ancillaryException,
                    ancillaryExceptionDetails,
                    setRentalPeriodColumns,
                    setAllRentalPeriodColumns,
                    setInitialRentalPeriodColumns,
                    setCarGroupRows,
                    setAllCarGroupRows,
                    setInitialCarGroupRows,
                    setMatrix,
                    setInitialMatrix,
                    buildMatrix
                });
                setLoading(false);
            } catch (err) {
                setLoading(false);
            }
        }
        // CREA ECCEZIONE
        else if (ancillaryException) {
            try {
                const ancillaryDetails: AncillaryDetail[] = await getAncillaryDetailsFromCopyAncillary({
                    originalAncillaryId: ancillaryException?.prcAncillary?.id as number,
                    destinationAncillaryId: ancillaryException?.prcAncillary?.id as number,
                    fixedDiscount: ancillaryException?.relatedDiscountIncreaseFixed ? Number(ancillaryException.relatedDiscountIncreaseFixed.toString().replace(",", ".")) : null,
                    percentDiscount: ancillaryException?.relatedDiscountIncreasePercentage ? Number(ancillaryException.relatedDiscountIncreasePercentage) : null
                });
                const ancillaryExceptionDetailsResponse: AncillaryExceptionDetail[] = ancillaryDetails.map((ancillaryDetail: AncillaryDetail) => {
                    const ancillaryExceptionDetail: AncillaryExceptionDetail = {
                        id: ancillaryDetail.id,
                        prcAncillaryException: ancillaryException,
                        carGroup: ancillaryDetail.carGroup,
                        prcRentalPeriod: ancillaryDetail.prcRentalPeriod,
                        price: ancillaryDetail.price,
                        sell: ancillaryDetail.sell,
                        forfait: ancillaryDetail?.prcAncillary?.tpUnit === "RENTAL" || ancillaryDetail?.prcAncillary?.subtype === "DELIVERY" || ancillaryDetail?.prcAncillary?.subtype === "COLLECTION" ? false : ancillaryDetail.forfait
                    };
                    return ancillaryExceptionDetail;
                });
                const ancillaryExceptionDetails = mapAncillaryExceptionDetails({ancillaryExceptionDetails: ancillaryExceptionDetailsResponse});

                setAncillaryExceptionDetails(ancillaryExceptionDetails);

                const sellList = await getSell();
                setSellList(sellList.map((sell: Sell) => {
                    return {
                        code: sell.sellType,
                        label: sell.description
                    };
                }));

                let rentalPeriods: RentalPeriodEntityResponse[];
                let carGroups: string[];
                let allRentalPeriods: RentalPeriodEntityResponse[];
                let allCarGroups: string[];

                // ancillary con matrice già esistente
                if (ancillaryExceptionDetails.length > 0) {
                    const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({ancillaryExceptionDetails});
                    rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                    carGroups = carGroupsAndRentalPeriods.carGroups;
                    allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                        return rp.tpUnit === ancillaryException?.tpUnit;
                    });

                    // togliere CAR_GROUPS || quando si pusha
                    // allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    allCarGroups = (await getOrderedAcriss(ancillaryException.legalEntity)).map(el => el.acriss);

                } else {
                    rentalPeriods = allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                        return rp.tpUnit === ancillaryException?.tpUnit;
                    });

                    // togliere CAR_GROUPS || quando si pusha
                    // carGroups = allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    carGroups = allCarGroups = (await getOrderedAcriss(ancillaryException.legalEntity)).map(el => el.acriss);
                }

                setCarGroupsAndRentalPeriodsAndMatrix({
                    rentalPeriods,
                    carGroups,
                    allRentalPeriods,
                    allCarGroups,
                    ancillaryException,
                    ancillaryExceptionDetails,
                    setRentalPeriodColumns,
                    setAllRentalPeriodColumns,
                    setInitialRentalPeriodColumns,
                    setCarGroupRows,
                    setAllCarGroupRows,
                    setInitialCarGroupRows,
                    setMatrix,
                    setInitialMatrix,
                    buildMatrix
                });
                setLoading(false);
            } catch (err) {
                setLoading(false);
            }
        }
    };

    // Use Effect
    useEffect(() => {
        getMatrixInfo();
        // eslint-disable-next-line
    }, []);

    const setCarGroupsAndRentalPeriodsAndMatrix = ({
                                                       rentalPeriods,
                                                       carGroups,
                                                       allRentalPeriods,
                                                       allCarGroups,
                                                       ancillaryException,
                                                       ancillaryExceptionDetails,
                                                       setRentalPeriodColumns,
                                                       setAllRentalPeriodColumns,
                                                       setInitialRentalPeriodColumns,
                                                       setCarGroupRows,
                                                       setAllCarGroupRows,
                                                       setInitialCarGroupRows,
                                                       setMatrix,
                                                       setInitialMatrix,
                                                       buildMatrix
                                                   }: {
        rentalPeriods: RentalPeriodEntityResponse[]
        carGroups: string[]
        allRentalPeriods: RentalPeriodEntityResponse[]
        allCarGroups: string[]
        ancillaryException: AncillaryExceptionEntityResponse
        ancillaryExceptionDetails: AncillaryExceptionDetail[]
        setRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setAllRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setInitialRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setCarGroupRows: Dispatch<SetStateAction<string[]>>
        setAllCarGroupRows: Dispatch<SetStateAction<string[]>>
        setInitialCarGroupRows: Dispatch<SetStateAction<string[]>>
        setMatrix: Dispatch<SetStateAction<MatrixDictionary>>
        setInitialMatrix: Dispatch<SetStateAction<MatrixDictionary>>
        buildMatrix: Function
    }) => {
        setRentalPeriodColumns(rentalPeriods);
        setCarGroupRows(carGroups);

        setAllRentalPeriodColumns(JSON.parse(JSON.stringify(allRentalPeriods)));
        setAllCarGroupRows(JSON.parse(JSON.stringify(allCarGroups)));
        setInitialRentalPeriodColumns(JSON.parse(JSON.stringify(rentalPeriods)));
        setInitialCarGroupRows(JSON.parse(JSON.stringify(carGroups)));

        //costruire le righe della matrice a partire dal car group e mettendo in fila i periodi di noleggio
        if (ancillaryExceptionDetails.length > 0) {
            const accumulator: MatrixDictionary = buildMatrix(ancillaryExceptionDetails, ancillaryException, rentalPeriods, carGroups);

            setMatrix(accumulator);
            setInitialMatrix(JSON.parse(JSON.stringify(accumulator)));
            setGridRefColumDefsAndRowData({
                rentalPeriods: rentalPeriods,
                carGroups: carGroups,
                matrix: accumulator
            });
        }
    };

    const populateCarGroupsAndRentalPeriods = ({ancillaryExceptionDetails}: { ancillaryExceptionDetails: AncillaryExceptionDetail[] }): { carGroups: string[], rentalPeriods: RentalPeriodEntityResponse[] } => {
        let rentalPeriods: RentalPeriodEntityResponse[] = [];
        let carGroups: string[] = [];

        ancillaryExceptionDetails.forEach(ancillaryExceptionDetail => {
            if (!rentalPeriods.find(rp => rp.id === ancillaryExceptionDetail.prcRentalPeriod.id)) {
                rentalPeriods.push(ancillaryExceptionDetail.prcRentalPeriod);
            }

            if (!carGroups.find(cg => cg === ancillaryExceptionDetail.carGroup)) {
                carGroups.push(ancillaryExceptionDetail.carGroup);
            }
        });
        return {
            carGroups,
            rentalPeriods
        };
    };

    const mapAncillaryExceptionDetails = ({ancillaryExceptionDetails}: { ancillaryExceptionDetails: AncillaryExceptionDetail[] }): AncillaryExceptionDetail[] => {
        return ancillaryExceptionDetails.map((ancillaryExceptionDetail: AncillaryExceptionDetail) => {
            return {
                ...ancillaryExceptionDetail,
                price: i18nConvertNumberWithDecimals(Number(ancillaryExceptionDetail.price) / 100)
            };
        });
    };

    const cellValueSetter = (params: any) => {
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue <= maxNumberYupLimit && params.newValue >= 0) {
            params.data[params.colDef.field.split(".")[0]] = {
                ...params.data[params.colDef.field.split(".")[0]],
                price: params.newValue
            };
            return true;
        } else return false;
    };

    const buildMatrixColDef = ({
                                   rentalPeriodColumns,
                                   t,
                                   ancillaryException
                               }: {
        rentalPeriodColumns: RentalPeriodEntityResponse[]
        t: Function
        ancillaryException: AncillaryExceptionEntityResponse | null;
    }): ColDef[] => {
        return [
            {
                headerName: t("pricing:ancillaryMatrix.labels.carGroup"),
                headerTooltip: t("pricing:ancillaryMatrix.labels.carGroup"),
                field: "carGroup",
                width: 120,
                editable: false,
                pinned: "left"
            },
            ...rentalPeriodColumns.map((rp: RentalPeriodEntityResponse) => ({
                    //COSTRUZIONE HEADER COLONNE GESTENDO IL FATTO CHE POSSANO ESSERE ANCHE GIORNI DELLA SETTIMANA
                    headerName: `${rp.name} 
                                (${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.from)?.label : rp.from}
                               - ${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.to)?.label : rp.to})`,
                    headerTooltip: `${rp.name} 
                                   (${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.from)?.label : rp.from}
                                  - ${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.to)?.label : rp.to})`,
                    headerClass: ["bordersRight"],
                    cellStyle: {textAlign: "center", padding: 5},
                    children: [
                        {
                            headerName: "",
                            field: rp.id + ".forfait",
                            headerClass: ["bordersRight"],
                            wrapText: true,
                            headerGroupComponent: (params: IHeaderGroupParams) => {
                                const currentDetail = Object.values(matrix)[0][rp.id];
                                return <CheckboxRenderer {...params}
                                                         width={100}
                                                         field={rp.id + ".forfait"}
                                                         disabled={readOnly || ancillaryException?.tpUnit === "RENTAL" || ancillaryException?.subtype === "DELIVERY" || ancillaryException?.subtype === "COLLECTION"}
                                                         defaultValue={currentDetail.forfait as boolean}
                                                         label={t("pricing:labels.forfait")}
                                                         onChange={(e: any) => {
                                                             const newValue = e.target.checked;
                                                             const newMatrix = {...matrix};
                                                             Object.values(newMatrix).forEach((value: any) => {
                                                                 value[rp.id].forfait = newValue;
                                                             });
                                                             setMatrix(newMatrix);
                                                         }}
                                />
                            },
                            children: [
                                {
                                    headerName: rp.tpUnit,
                                    headerTooltip: rp.tpUnit,
                                    field: rp.id + ".price",
                                    width: rentalPeriodColumns?.length > 6 ? 100 : null,
                                    flex: rentalPeriodColumns?.length > 6 ? null : 1,
                                    resizable: true,
                                    editable: !readOnly,
                                    cellEditor: "customEditor",
                                    valueSetter: cellValueSetter,
                                    cellRenderer: (params: ICellRendererParams) => {
                                        return ancillaryException?.chargeType === "VALUE" ?
                                            i18nConvertNumberWithDecimals(params.value)
                                            :
                                            `${Number(params.value)}%`;
                                    },
                                    cellStyle: {
                                        justifyContent: "flex-end",
                                        textAlign: "right",
                                        borderRight: "1px dotted #E0E0E0",
                                        padding: 5
                                    }
                                },
                                {
                                    headerName: `${t("pricing:ancillaryMatrix.labels.sellType")}*`,
                                    headerTooltip: `${t("pricing:ancillaryMatrix.labels.sellType")}*`,
                                    field: rp.id + ".sell",
                                    tooltipField: rp.id + ".sell",
                                    editable: !readOnly,
                                    width: 200,
                                    resizable: true,
                                    cellEditor: "agRichSelectCellEditor",
                                    cellEditorPopup: true,
                                    cellEditorParams: {
                                        values: sellList.map(s => s.code),
                                        cellHeight: 20,
                                        width: 250,
                                        formatValue: (value: string) => value?.toUpperCase(),
                                        cellRenderer: (params: ICellRendererParams) =>
                                            <div>{params.value?.toUpperCase()}</div>
                                    },
                                    cellRenderer: (params: ICellRendererParams) =>
                                        getLabelChip(params.value?.toUpperCase()),
                                    headerClass: ["bordersRight"],
                                    cellStyle: (params: CellClassParams) => ({
                                        justifyContent: "center",
                                        borderRight: "2px solid #E0E0E0",
                                        backgroundColor: !sellList.map(sell => sell.code).includes(params.value as string) ? theme.palette.error.light : undefined
                                    })
                                }
                            ]
                        },
                    ]
                }
            ))
        ];
    };

    const buildMatrixDictionary = (ancillaryExceptionDetails: AncillaryExceptionDetail[]): MatrixDictionary => {
        return ancillaryExceptionDetails.reduce((dictionary: MatrixDictionary, detail) => {
            return {
                ...dictionary,
                [detail.carGroup]: {
                    ...dictionary[detail.carGroup],
                    [detail.prcRentalPeriod.id]: detail
                }
            };
        }, {});
    };

    const insertAncillaryExceptionDetailInMatrix = ({
                                                        matrixDictionary,
                                                        carGroup,
                                                        rentalPeriod,
                                                        ancillaryExceptionDetail
                                                    }: { matrixDictionary: MatrixDictionary, carGroup: string, rentalPeriod: RentalPeriodEntityResponse, ancillaryExceptionDetail: AncillaryExceptionDetail }): MatrixDictionary => {
        const currentRowObject: MatrixElementInterface = matrixDictionary[carGroup];
        if (currentRowObject) matrixDictionary[carGroup] = {
            ...matrixDictionary[carGroup],
            [rentalPeriod.id]: ancillaryExceptionDetail
        };
        else matrixDictionary[carGroup] = {
            [rentalPeriod.id]: ancillaryExceptionDetail
        };
        return matrixDictionary;
    };

    const buildEmptyAncillaryExceptionDetail = ({
                                                    ancillaryException,
                                                    carGroup,
                                                    rentalPeriod
                                                }: {
        ancillaryException: AncillaryExceptionEntityResponse,
        carGroup: string,
        rentalPeriod: RentalPeriodEntityResponse
    }): AncillaryExceptionDetail => {
        return {
            price: "0",
            sell: "",
            carGroup: carGroup,
            prcAncillaryException: ancillaryException,
            prcRentalPeriod: rentalPeriod,
            forfait: false
        };
    };

    const buildMatrix = (ancillaryExceptionDetails: AncillaryExceptionDetail[], ancillaryException: AncillaryExceptionEntityResponse, rentalPeriods: RentalPeriodEntityResponse[], carGroups: string[]): MatrixDictionary => {
        let matrixDictionary: MatrixDictionary = buildMatrixDictionary(ancillaryExceptionDetails);

        // per ciascuna riga, per ciascuna colonna, se esiste un dettaglio ce lo inserisco, se non esiste ne creo uno con valori a 0
        carGroups.forEach((carGroup: string) => {
            rentalPeriods.forEach((rentalPeriod: RentalPeriodEntityResponse) => {
                const firstLevel = matrixDictionary[carGroup];
                let currentDetail: AncillaryExceptionDetail | undefined;
                if (firstLevel) currentDetail = firstLevel[rentalPeriod.id];

                // se esiste un dettaglio ce lo inserisco
                if (currentDetail) {
                    matrixDictionary = insertAncillaryExceptionDetailInMatrix({
                        matrixDictionary,
                        carGroup,
                        rentalPeriod,
                        ancillaryExceptionDetail: currentDetail
                    });
                }
                // se non esiste ne creo uno con valori a 0
                else {
                    const currentEmptyDetail: AncillaryExceptionDetail = buildEmptyAncillaryExceptionDetail({
                        ancillaryException,
                        carGroup,
                        rentalPeriod
                    });
                    matrixDictionary = insertAncillaryExceptionDetailInMatrix({
                        matrixDictionary,
                        carGroup,
                        rentalPeriod,
                        ancillaryExceptionDetail: currentEmptyDetail
                    });
                }
            });
        });

        return matrixDictionary;
    };

    // ************ AG GRID START ************ //
    const columnDefs: ColDef[] = buildMatrixColDef({
        rentalPeriodColumns,
        t,
        ancillaryException
    });

    const CustomTooltip = (props: ITooltipParams & { color: string }) => {
        const sellType = sellList?.find(sell => sell.code === props.value);
        return (
            <>
                {sellType ? getChip(sellType) : null}
            </>
        );
    };

    const defaultColDef = {
        ...DEFAULT_MATRIX_COL_DEF,
        tooltipComponent: CustomTooltip
    };
    // ************ AG GRID - END ************ //

    const checkMatrix = (): AncillaryExceptionDetail[] | undefined => {
        const rowsFromAgGrid: MatrixElementInterface[] = getRowsFromAgGrid();
        let wrongSellType = false;

        try {
            const persistArray: AncillaryExceptionDetail[] = [];

            for (const matrixElementInterface of rowsFromAgGrid) {
                Object.values(matrixElementInterface).forEach((ancillaryExceptionDetail: AncillaryExceptionDetail) => persistArray.push({
                    ...ancillaryExceptionDetail,
                    price: convertToCents(ancillaryExceptionDetail.price),
                }));
            }

            const ancillaryDetailWithNumericSell = persistArray.find(ancillaryDetail => {
                if (!sellList.map(sell => sell.code)
                    .includes(ancillaryDetail.sell as string)
                ) {
                    return ancillaryDetail;
                }
            });
            if (ancillaryDetailWithNumericSell) {
                wrongSellType = true;
                throw t("pricing:ancillaryMatrix.errors.wrongSellType");
            } else {
                if (ancillaryException) {
                    return persistArray;
                }
            }
        } catch (err) {
            console.log(err);
            if (wrongSellType) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.wrongSellType"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    const handlePersistMatrix = async (persistArray: AncillaryExceptionDetail[]) => {
        try {
            if (ancillaryException && persistArray) {
                const ancillaryExceptionUpdated: AncillaryExceptionEntityResponse = {
                    ...ancillaryException,
                    used: ancillaryExceptionInUse
                }
                ancillaryExceptionUpdated.taxCode = (() => {
                    let resolved = ancillaryExceptionUpdated.taxCode;
                    if (typeof resolved === 'object' && resolved !== null) {
                        resolved = (resolved as unknown as TaxCodeObject).value;
                    }
                    return serviceItem.find(
                        (item: any) => item.name === resolved
                    )?.code || serviceItem.find(
                        (item: any) => item.code === resolved
                    )?.code || resolved;
                })();

                await persistAncillaryExceptionAndDetails({
                    prcAncillaryException: ancillaryExceptionUpdated,
                    ancillaryExceptionDetails: persistArray
                });

                setDialog(null);
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);

                navigate("../ancillary/ancillary-exception");
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleDeleteMatrix = async () => {
        try {
            setDialog(null);

            let ancillaryExceptionDetails: AncillaryExceptionDetail[] = [];
            //se ancillary exception details è vuoto, reinizializzare la matrice da accessorio
            if (ancillaryExceptionDetails?.length === 0) {
                const ancillaryDetails: AncillaryDetail[] = await getAncillaryDetailsFromCopyAncillary({
                    originalAncillaryId: ancillaryException?.prcAncillary?.id as number,
                    destinationAncillaryId: ancillaryException?.prcAncillary?.id as number,
                    fixedDiscount: ancillaryException?.relatedDiscountIncreaseFixed ? Number(ancillaryException.relatedDiscountIncreaseFixed.toString().replace(",", ".")) : null,
                    percentDiscount: ancillaryException?.relatedDiscountIncreasePercentage ? Number(ancillaryException.relatedDiscountIncreasePercentage) : null
                });

                const ancillaryExceptionDetailsResponse: AncillaryExceptionDetail[] = ancillaryDetails.map((ancillaryDetail: AncillaryDetail) => {
                    const ancillaryExceptionDetail: AncillaryExceptionDetail = {
                        id: ancillaryDetail.id,
                        prcAncillaryException: ancillaryException as AncillaryExceptionEntityResponse,
                        carGroup: ancillaryDetail.carGroup,
                        prcRentalPeriod: ancillaryDetail.prcRentalPeriod,
                        price: ancillaryDetail.price,
                        sell: ancillaryDetail.sell,
                        forfait: ancillaryDetail?.prcAncillary?.tpUnit === "RENTAL" || ancillaryDetail?.prcAncillary?.subtype === "DELIVERY" || ancillaryDetail?.prcAncillary?.subtype === "COLLECTION" ? false : ancillaryDetail.forfait
                    };
                    return ancillaryExceptionDetail;
                });
                ancillaryExceptionDetails = mapAncillaryExceptionDetails({ancillaryExceptionDetails: ancillaryExceptionDetailsResponse});
            }

            setAncillaryExceptionDetails(ancillaryExceptionDetails);

            const sellList = await getSell();
            setSellList(sellList.map((sell: Sell) => {
                return {
                    code: sell.sellType,
                    label: sell.description
                };
            }));

            let rentalPeriods: RentalPeriodEntityResponse[];
            let carGroups: string[];
            let allRentalPeriods: RentalPeriodEntityResponse[];
            let allCarGroups: string[];

            // ancillary con matrice già esistente
            if (ancillaryExceptionDetails.length > 0) {
                const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({ancillaryExceptionDetails});
                rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                carGroups = carGroupsAndRentalPeriods.carGroups;
                allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                    return rp.tpUnit === ancillaryException?.tpUnit;
                });

                // togliere CAR_GROUPS || quando si pusha
                // allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                allCarGroups = (await getOrderedAcriss((ancillaryException as AncillaryExceptionEntityResponse).legalEntity)).map(el => el.acriss);

            } else {
                rentalPeriods = allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                    return rp.tpUnit === ancillaryException?.tpUnit;
                });

                // togliere CAR_GROUPS || quando si pusha
                // carGroups = allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                carGroups = allCarGroups = (await getOrderedAcriss((ancillaryException as AncillaryExceptionEntityResponse).legalEntity)).map(el => el.acriss);
            }

            setCarGroupsAndRentalPeriodsAndMatrix({
                rentalPeriods,
                carGroups,
                allRentalPeriods,
                allCarGroups,
                ancillaryException: ancillaryException as AncillaryExceptionEntityResponse,
                ancillaryExceptionDetails,
                setRentalPeriodColumns,
                setAllRentalPeriodColumns,
                setInitialRentalPeriodColumns,
                setCarGroupRows,
                setAllCarGroupRows,
                setInitialCarGroupRows,
                setMatrix,
                setInitialMatrix,
                buildMatrix
            });
            setLoading(false);

            // navigate("../ancillary/ancillary-exception");

        } catch (err) {
        }
    };

    const setGridRefColumDefsAndRowData = ({
                                               rentalPeriods,
                                               carGroups,
                                               matrix
                                           }:
                                               {
                                                   rentalPeriods: RentalPeriodEntityResponse[]
                                                   carGroups: string[]
                                                   matrix: MatrixDictionary
                                               }) => {
        const columnDefs: ColDef[] = buildMatrixColDef({
            rentalPeriodColumns: rentalPeriods,
            t,
            ancillaryException
        });

        gridRef?.current?.api?.setColumnDefs(columnDefs);
        gridRef?.current?.api?.setRowData(Object.entries(matrix).map(([key, value]) => {
            return {
                carGroup: key,
                ...value
            };
        }));
        gridRef?.current?.api?.refreshHeader();
        gridRef?.current?.api?.refreshCells();

        setMatrix(matrix);

        setCarGroupRows(carGroups.length === 0 ? initialCarGroupRows : carGroups);
        setRentalPeriodColumns(rentalPeriods.length === 0 ? initialRentalPeriodColumns : rentalPeriods);
    };

    const handleCancel = () => {
        setDialog(null);
        const accumulator = JSON.parse(JSON.stringify({...initialMatrix}));

        setGridRefColumDefsAndRowData({
            rentalPeriods: initialRentalPeriodColumns,
            carGroups: initialCarGroupRows,
            matrix: accumulator
        });
        navigate("../ancillary/ancillary-exception");
    };

    const getRowsFromAgGrid = () => {
        const rowsFromAgGrid: MatrixElementInterface[] = [];
        if (gridApi) gridApi.forEachNodeAfterFilterAndSort((node: any) => {
            const newNodeData = Object.entries(node.data)
                .filter(([key]: [string, any]) => key !== "carGroup")
                .reduce((acc: any, [key, value]: [string, any]) => {
                    return {
                        ...acc,
                        [key]: {...value}
                    };
                }, {});
            rowsFromAgGrid.push(newNodeData);
        });
        return rowsFromAgGrid;
    };

    const getChip = (s: SelectElementModelFE) => {
        let color;
        switch (s.code) {
            case("A"): {
                color = "#BEE5CA";
                break;
            }
            case("N"): {
                color = theme.palette.neutral.light;
                break;
            }
            case("Z"): {
                color = "#99D9F2";
                break;
            }
            case("S"): {
                color = "#FBEC9B";
                break;
            }
            case("B"): {
                color = "#E999CB";
                break;
            }
            case("R"): {
                color = "#F39D9D";
                break;
            }
            default: {
                break;
            }
        }
        return <Chip label={`${s.code} - ${s.label}`}
                     sx={{bgcolor: color}}
                     variant={"filled"}/>;
    };

    const getLabelChip = (sellType: string) => {
        let color;
        switch (sellType) {
            case("A"): {
                color = "#BEE5CA";
                break;
            }
            case("N"): {
                color = theme.palette.neutral.light;
                break;
            }
            case("Z"): {
                color = "#99D9F2";
                break;
            }
            case("S"): {
                color = "#FBEC9B";
                break;
            }
            case("B"): {
                color = "#E999CB";
                break;
            }
            case("R"): {
                color = "#F39D9D";
                break;
            }
            default: {
                break;
            }
        }
        return <Chip label={`${sellType}`}
                     sx={{bgcolor: color, px: 2}}
                     variant={"filled"}/>;
    };

    function processCellFromClipboard(params: ProcessCellForExportParams) {
        // @ts-ignore
        if (!isNaN(params.value) && params.column.colId.includes("price")) {
            return params.value;
            // @ts-ignore
        } else if (params.column.colId.includes("sell") && sellList.map(sell => sell.code).includes(params.value)) {
            return params.value;
        } else {
            return "";
        }
    }

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && ancillaryExceptionInUse &&
                <CustomBanner message={t("pricing:ancillaryExceptionMatrix.banner")} severity={"warning"}/>}
            {ancillaryExceptionId
                && ancillaryExceptionDetails?.length > 0
                && ancillaryExceptionDetails[0].id === null
                &&
                <CustomBanner message={t("pricing:ancillaryExceptionMatrix.restoredMatrix")} severity={"warning"}/>
            }
            {loading ?
                null
                :
                Object.entries(matrix).length > 0 ?
                    // Matrix
                    <Grid item xs={12}>
                        <AgGridCustomTable
                            gridRef={gridRef}
                            stopEditingWhenCellsLoseFocus
                            onGridReady={onGridReady}
                            defaultColDef={defaultColDef}
                            columnDefs={columnDefs}
                            height={heightDynamicMatrix(carGroupRows as string[], 145)}
                            frameworkComponents={{
                                customEditor: ancillaryException?.chargeType === "VALUE" ? NumberCustomEditorComponent : PercentageCellEditor
                            }}
                            pagination={false}
                            localeText={agGridLocale}
                            tooltipShowDelay={0}
                            rowData={Object.entries(matrix).map(([key, value]) => {
                                return {
                                    carGroup: key,
                                    ...value
                                };
                            })}
                            rowSelection={"multiple"}
                            suppressRowClickSelection={true}
                            enableRangeSelection={true}
                            enableCellTextSelection={false}
                            processCellFromClipboard={processCellFromClipboard}
                        />
                        {/* sell types legend */}
                        <Grid container item xs={12} columnGap={2} my={2}>
                            {t("pricing:ancillaryMatrix.labels.sellType")}*:{sellList.map((s: SelectElementModelFE) => getChip(s))}
                        </Grid>
                        {!readOnly &&
                            <Grid container item xs={12} justifyContent="space-between" columnGap={2} my={2}>
                                <Grid>
                                    {ancillaryExceptionDetails?.length > 0
                                        && ancillaryExceptionDetails[0].id !== null
                                        && <Button color={"accent"}
                                                   type={"button"}
                                                   onClick={handleDeleteMatrix}
                                        >
                                            {t("pricing:ancillaryMatrix.actions.restoreFromAncillaryMatrix")}
                                        </Button>
                                    }
                                </Grid>
                                <Grid>
                                    {/* { ancillaryExceptionId && */}
                                    <Button variant="outlined"
                                            sx={{marginRight: "15px"}}
                                            color={"accent"}
                                            type={"button"}
                                            onClick={() => setDialog({
                                                open: true,
                                                message: t("pricing:dialogs.messages.looseChanging"),
                                                onClickConfirm: () => {
                                                    window.close()
                                                }
                                            })}
                                    >
                                        {t("pricing:actions.back")}
                                    </Button>
                                    {/* } */}
                                    <Button color={"accent"}
                                            type={"button"}
                                            onClick={() => {
                                                const persistArray = checkMatrix();
                                                if (persistArray) {
                                                    setDialog({
                                                        open: true,
                                                        message: ancillaryExceptionId ?
                                                            (
                                                                ancillaryExceptionInUse ?
                                                                    t("pricing:ancillaryExceptionMatrix.banner")
                                                                    :
                                                                    t("pricing:ancillaryExceptionMatrix.dialogContents.updateAncillaryExceptionMatrix")
                                                            )
                                                            :
                                                            t("pricing:ancillaryExceptionMatrix.dialogContents.createAncillaryExceptionMatrix"),
                                                        onClickConfirm: () => handlePersistMatrix(persistArray)
                                                    });
                                                }
                                            }}
                                    >
                                        {t("pricing:actions.confirm")}
                                    </Button>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                    :
                    readOnly ?
                        ancillaryException?.prcAncillary?.isOneWay ?
                            null
                            :
                            <Grid item xs={12} justifyContent={"center"} p={2}>
                                {t("pricing:ancillaryExceptionMatrixInsert.noMatrix")}
                            </Grid>
                        : null
            }
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
        </Grid>
    );
};

export default AncillaryExceptionMatrix;